import React, { useState } from 'react';
import { Controller, type Control, useWatch } from 'react-hook-form';
import { Trash2 } from 'lucide-react';
import { formatRupiah, parseRupiah } from '../../utils/format';
import type { FinishedGoodsProductionFormValues } from '../../hooks/inventory-hooks/useFinishedGoodsProductionForm';
import { useLanguage } from '../../features/language/useLanguage';

interface FinishedGoodsSummarySectionProps {
  control: Control<FinishedGoodsProductionFormValues>;
  miscCostFields: { rowId: string }[];
  onAddMiscCost: () => void;
  onRemoveMiscCost: (index: number) => void;
  totalInputKg: number;
  finalQuantityPcs: number;
  totalInputCost: number;
  unitCostPerPiece: number;
  estimatedSellingPrice?: number;
  onEstimatedSellingPriceChange?: (value: number) => void;
  readOnly?: boolean;
}

export const FinishedGoodsSummarySection: React.FC<FinishedGoodsSummarySectionProps> = ({
  control,
  miscCostFields,
  onAddMiscCost,
  onRemoveMiscCost,
  totalInputKg,
  finalQuantityPcs,
  totalInputCost,
  unitCostPerPiece,
  estimatedSellingPrice,
  onEstimatedSellingPriceChange,
  readOnly = false,
}) => {
  const { t } = useLanguage();
  const kgPerPiece = finalQuantityPcs > 0 ? totalInputKg / finalQuantityPcs : 0;
  const miscCosts = useWatch({ control, name: 'miscCosts' }) || [];
  const [unitDraftByRowId, setUnitDraftByRowId] = useState<Record<string, string>>({});

  return (
    <div className="space-y-6 rounded-xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
      {/* Header Section */}
      <div>
        <h3 className="text-base font-semibold text-gray-900">{t('inventory.finishedGoods.summary.title')}</h3>
        <p className="text-xs text-gray-500">{t('inventory.finishedGoods.summary.detailCalculation')}</p>
      </div>

      {/* Primary Stats Grid (Physical) */}
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg bg-gray-50 p-3">
          <p className="text-[10px] font-medium uppercase tracking-wider text-gray-500">{t('inventory.finishedGoods.summary.totalInput')}</p>
          <p className="text-lg font-bold text-gray-900">
            {totalInputKg.toFixed(3)} <span className="text-sm font-normal text-gray-500">{t('inventory.history.unitKg')}</span>
          </p>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <p className="text-[10px] font-medium uppercase tracking-wider text-gray-500">{t('inventory.finishedGoods.summary.weightPerPcs')}</p>
          <p className="text-lg font-bold text-gray-900">
            {kgPerPiece.toFixed(3)} <span className="text-sm font-normal text-gray-500">{t('inventory.history.unitKg')}</span>
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 sm:p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{t('inventory.finishedGoods.summary.miscellaneousCost')}</h3>
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={onAddMiscCost}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-900 hover:bg-gray-50"
            >
              {t('inventory.finishedGoods.actions.add')}
            </button>
          )}
        </div>

        <div className="mt-3">
          {/* Column headers — desktop only; on mobile each row carries its own labels */}
          <div className="hidden gap-2 px-1 pb-1 md:grid md:grid-cols-12">
            <div className="col-span-4 text-xs font-semibold text-gray-500">{t('forms.customer.name')}</div>
            <div className="col-span-3 text-xs font-semibold text-gray-500">{t('reports.details.unitPrice')}</div>
            <div className="col-span-2 text-xs font-semibold text-gray-500">{t('reports.export.columns.unit')}</div>
            <div className="col-span-2 text-xs font-semibold text-gray-500">{t('reports.details.total')}</div>
            {!readOnly && <div className="col-span-1" />}
          </div>

          <div className="space-y-3">
            {miscCostFields.map((field, index) => {
              const unitPrice = typeof miscCosts[index]?.unitPrice === 'number' ? miscCosts[index].unitPrice : 0;
              const unit = typeof miscCosts[index]?.unit === 'number' ? miscCosts[index].unit : 0;
              const total = unitPrice * unit;
              return (
                <div
                  key={field.rowId}
                  className="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-white p-3 md:grid-cols-12 md:items-end md:gap-2 md:rounded-none md:border-0 md:bg-transparent md:p-0"
                >
                  {/* Name */}
                  <div className="md:col-span-4">
                    <label className="mb-1 block text-xs font-semibold text-gray-700 md:hidden">{t('forms.customer.name')}</label>
                    <Controller
                      control={control}
                      name={`miscCosts.${index}.name`}
                      render={({ field: f }) => (
                        <input
                          {...f}
                          type="text"
                          disabled={readOnly}
                          className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          placeholder={t('inventory.finishedGoods.summary.packaging')}
                        />
                      )}
                    />
                  </div>

                  {/* Unit price + Unit — paired on mobile, flow into the grid on desktop */}
                  <div className="grid grid-cols-2 gap-3 md:contents">
                    <div className="md:col-span-3">
                      <label className="mb-1 block text-xs font-semibold text-gray-700 md:hidden">{t('reports.details.unitPrice')}</label>
                      <Controller
                        control={control}
                        name={`miscCosts.${index}.unitPrice`}
                        render={({ field: f }) => (
                          <input
                            type="text"
                            inputMode="numeric"
                            value={formatRupiah(f.value)}
                            onChange={(e) => {
                              if (readOnly) return;
                              f.onChange(parseRupiah(e.target.value));
                            }}
                            disabled={readOnly}
                            className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder={t('inventory.finishedGoods.summary.currencyPlaceholder')}
                          />
                        )}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-xs font-semibold text-gray-700 md:hidden">{t('reports.export.columns.unit')}</label>
                      <Controller
                        control={control}
                        name={`miscCosts.${index}.unit`}
                        render={({ field: f }) => (
                          <input
                            type="number"
                            inputMode="decimal"
                            value={
                              unitDraftByRowId[field.rowId] ??
                              (Number.isFinite(f.value) ? String(f.value) : '0')
                            }
                            onChange={(e) => {
                              if (readOnly) return;
                              const raw = e.target.value;
                              setUnitDraftByRowId(prev => ({ ...prev, [field.rowId]: raw }));
                              f.onChange(raw === '' ? 0 : Number(raw));
                            }}
                            onFocus={(e) => {
                              const existing = unitDraftByRowId[field.rowId];
                              if (existing === undefined) {
                                setUnitDraftByRowId(prev => ({ ...prev, [field.rowId]: Number.isFinite(f.value) ? String(f.value) : '0' }));
                              }
                              if ((existing ?? (Number.isFinite(f.value) ? String(f.value) : '0')) === '0') {
                                e.currentTarget.select();
                              }
                            }}
                            onBlur={() => {
                              setUnitDraftByRowId(prev => {
                                const next = { ...prev };
                                delete next[field.rowId];
                                return next;
                              });
                              f.onBlur();
                            }}
                            disabled={readOnly}
                            className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder={t('inventory.finishedGoods.summary.unitPlaceholder')}
                            min={0}
                            step="1"
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Total + delete — paired on mobile, flow into the grid on desktop */}
                  <div className="flex items-end justify-between gap-3 md:contents">
                    <div className="min-w-0 flex-1 md:col-span-2">
                      <label className="mb-1 block text-xs font-semibold text-gray-700 md:hidden">{t('reports.details.total')}</label>
                      <div className="w-full rounded-lg border border-gray-300 bg-gray-100 px-3 py-2 text-sm tabular-nums text-gray-900">
                        {formatRupiah(total)}
                      </div>
                    </div>
                    {!readOnly && (
                      <div className="flex-none md:col-span-1 md:flex md:justify-end">
                        <button
                          type="button"
                          onClick={() => onRemoveMiscCost(index)}
                          disabled={miscCostFields.length <= 1}
                          className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                          aria-label={t('inventory.finishedGoods.actions.remove')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Input Section */}
      <div className="relative">
        <label className="mb-1.5 block text-xs font-semibold text-gray-700">
          {t('inventory.finishedGoods.summary.estimatedSellingPricePerPcs')}
        </label>
        <div className="relative overflow-hidden rounded-lg border border-gray-300 transition-focus-within focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
          <input
            type="text"
            inputMode="numeric"
            value={formatRupiah(estimatedSellingPrice)}
            onChange={e => {
              if (readOnly) return;
              onEstimatedSellingPriceChange?.(parseRupiah(e.target.value));
            }}
            disabled={readOnly}
            className="block w-full border-0 bg-transparent py-2.5 px-3 text-sm placeholder-gray-400 focus:ring-0 disabled:bg-gray-100 disabled:cursor-not-allowed"
            placeholder={t('inventory.finishedGoods.summary.currencyPlaceholder')}
            data-test-id="finished-goods-estimated-price"
          />
        </div>
      </div>

      {/* Secondary Stats Grid (Financial) */}
      <div className="grid grid-cols-2 gap-4 border-t border-gray-100 pt-5">
        <div className="rounded-lg bg-blue-50/50 p-3 border border-blue-100/50">
          <p className="text-[10px] font-medium uppercase tracking-wider text-gray-900">{t('inventory.finishedGoods.summary.totalCogs')}</p>
          <p className="text-lg font-bold text-gray-900">{formatRupiah(totalInputCost)}</p>
        </div>
        <div className="rounded-lg bg-gray-100 p-3 shadow-md shadow-blue-200">
          <p className="text-[10px] font-medium uppercase tracking-wider text-gray-900">{t('inventory.finishedGoods.summary.unitCostPerPcs')}</p>
          <p className="text-lg font-bold text-gray-900">{formatRupiah(unitCostPerPiece)}</p>
        </div>
      </div>
    </div>
  );
};
